import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import {
  Users, CreditCard, Calendar, MessageSquare,
  TrendingUp, Vote, BarChart3, UsersRound, Mail,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip,
} from "recharts";
import { Card, KPICard, PageHeader, Spinner } from "./_ui";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return "—"; }
}

const OverviewRow = ({ icon: Icon, label, value, color }) => (
  <div className="flex items-center justify-between px-5 py-3.5 border-t border-white/5 first:border-t-0">
    <div className="flex items-center gap-3">
      <div
        className="w-9 h-9 rounded-lg grid place-items-center"
        style={{ background: `${color}1A`, color }}
      >
        <Icon size={16} />
      </div>
      <span className="text-sm text-slate-200">{label}</span>
    </div>
    <span className="font-display text-xl text-white">{value}</span>
  </div>
);

export default function DashboardPanel() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) {
    return (
      <>
        <PageHeader title="Tableau de bord" subtitle="Vue d'ensemble en temps réel du mouvement A.F.B." />
        <Spinner />
      </>
    );
  }

  const { kpis, overview, coord_breakdown, latest_news, next_events } = data;

  return (
    <div data-testid="admin-dashboard">
      <PageHeader title="Tableau de bord" subtitle="Vue d'ensemble en temps réel du mouvement A.F.B." />

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          icon={Users} label="Membres actifs" value={kpis.members_active}
          delta="+12%" color="blue" testid="kpi-members"
        />
        <KPICard
          icon={CreditCard} label="Cotisations (mois)" value={`${kpis.cotis_month}€`}
          delta="+8%" color="green" testid="kpi-cotis"
        />
        <KPICard
          icon={Calendar} label="Événements" value={kpis.events}
          color="orange" testid="kpi-events"
        />
        <KPICard
          icon={MessageSquare} label="Messages non lus" value={kpis.unread_messages}
          color="purple" testid="kpi-messages"
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        {/* Members by coordination - bar chart */}
        <Card className="lg:col-span-2">
          <div className="px-5 pt-5 pb-2">
            <h3 className="text-[15px] font-semibold text-white tracking-tight">Membres par coordination</h3>
          </div>
          <div className="px-2 pb-4 h-[280px]" data-testid="chart-coordinations">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={coord_breakdown} margin={{ top: 20, right: 16, left: 0, bottom: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis
                  dataKey="name"
                  tick={{ fill: "#94A3B8", fontSize: 11 }}
                  tickLine={false}
                  axisLine={{ stroke: "rgba(255,255,255,0.08)" }}
                  tickFormatter={(v) => (v.length > 14 ? v.slice(0, 13) + "…" : v)}
                />
                <YAxis
                  tick={{ fill: "#94A3B8", fontSize: 11 }}
                  tickLine={false}
                  axisLine={{ stroke: "rgba(255,255,255,0.08)" }}
                  allowDecimals={false}
                />
                <Tooltip
                  cursor={{ fill: "rgba(255,255,255,0.04)" }}
                  contentStyle={{
                    background: "#0B1220",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 8,
                    color: "#E2E8F0",
                    fontSize: 12,
                  }}
                />
                <Bar dataKey="members" fill="#3B82F6" radius={[6, 6, 0, 0]} maxBarSize={48} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Vue d'ensemble */}
        <Card>
          <div className="px-5 pt-5 pb-3">
            <h3 className="text-[15px] font-semibold text-white tracking-tight">Vue d'ensemble</h3>
          </div>
          <div>
            <OverviewRow icon={TrendingUp} label="Actualités publiées" value={overview.news_published} color="#34D399" />
            <OverviewRow icon={Vote} label="Votes en cours" value={overview.votes_active} color="#FBBF24" />
            <OverviewRow icon={BarChart3} label="Sondages actifs" value={overview.polls_active} color="#A78BFA" />
            <OverviewRow icon={UsersRound} label="Abonnés newsletter" value={overview.newsletter} color="#34D399" />
            <OverviewRow icon={Mail} label="Messages contact" value={overview.contact_messages} color="#F87171" />
          </div>
        </Card>
      </div>

      {/* News + Events row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
        <Card>
          <div className="px-5 pt-5 pb-3 flex items-center gap-2">
            <TrendingUp size={15} className="text-[#34D399]" />
            <h3 className="text-[15px] font-semibold text-white tracking-tight">Dernières actualités</h3>
          </div>
          <div className="px-2 pb-3">
            {latest_news.length === 0 ? (
              <div className="px-3 py-6 text-sm text-slate-500">Aucune actualité publiée.</div>
            ) : (
              latest_news.map((n) => (
                <Link
                  key={n.id}
                  to={`/actualites/${n.id}`}
                  className="block px-3 py-3 rounded-lg hover:bg-white/[0.03] border-t border-white/5 first:border-t-0"
                  data-testid={`dash-news-${n.id}`}
                >
                  <div className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] mt-2 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">{n.title}</div>
                      <div className="text-[11px] text-slate-500 mt-1">{fmtDate(n.published_at)}</div>
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </Card>

        <Card>
          <div className="px-5 pt-5 pb-3 flex items-center gap-2">
            <Calendar size={15} className="text-[#FBBF24]" />
            <h3 className="text-[15px] font-semibold text-white tracking-tight">Prochains événements</h3>
          </div>
          <div className="px-5 pb-5">
            {next_events.length === 0 ? (
              <div className="text-sm text-slate-500 py-3">Aucun événement à venir.</div>
            ) : (
              next_events.map((e) => {
                const d = new Date(e.starts_at);
                return (
                  <div key={e.id} className="flex items-start gap-4 py-3 border-t border-white/5 first:border-t-0">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#D4AF37]/20 to-[#0052CC]/20 border border-[#D4AF37]/30 grid place-items-center text-[#D4AF37] font-display text-lg shrink-0">
                      {d.getDate()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white">{e.title}</div>
                      <div className="text-[11px] text-slate-400 mt-1">{e.location}</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
